'use client';
import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import { MOCK_ITEMS } from '@/app/item-master-management/components/ItemMasterContent';

interface GRNWizardForm {
  vendor: string;
  site: string;
  poReference: string;
  deliveryDate: string;
  deliveryNote: string;
  notes: string;
  lineItems: {
    itemId: string;
    receivedQty: number;
    rejectedQty: number;
    unitCost: number;
    batchNumber: string;
    expiryDate: string;
  }[];
}

const SITES = ['Site Alpha', 'Site Beta', 'Site Gamma', 'Warehouse B'];
const VENDORS = ['Tata Steel Ltd', 'Siemens India Pvt Ltd', 'Bosch Rexroth AG', 'ABB Limited', 'Safety First Pvt Ltd', 'Castrol India Ltd', 'Weldex Consumables'];

export default function GRNWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, control, watch, getValues, formState: { errors } } = useForm<GRNWizardForm>({
    defaultValues: {
      lineItems: [{ itemId: '', receivedQty: 0, rejectedQty: 0, unitCost: 0, batchNumber: '', expiryDate: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lineItems' });
  const lineItems = watch('lineItems');

  const totalValue = lineItems.reduce((acc, li) => {
    const item = MOCK_ITEMS.find((i) => i.id === li.itemId);
    const qty = li.receivedQty - li.rejectedQty;
    return acc + qty * (li.unitCost || item?.unitCost || 0);
  }, 0);

  const handleNext = () => {
    if (step < 3) setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    // BACKEND INTEGRATION POINT: POST /api/v1/grgi/grn
    await new Promise((r) => setTimeout(r, 1400));
    setIsSubmitting(false);
    toast.success('GRN created successfully and submitted for inspection.');
    onClose();
    setStep(1);
  };

  const STEPS = [
    { num: 1, label: 'Header Details' },
    { num: 2, label: 'Line Items' },
    { num: 3, label: 'Review & Submit' },
  ];

  const fieldClass = (hasError: boolean) =>
    `w-full px-3 py-2 border rounded-lg text-sm bg-input text-foreground placeholder:text-muted-foreground/60 outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all ${
      hasError ? 'border-red-400' : 'border-border'
    }`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create New GRN"
      subtitle="Step 1 of 3 — Goods Receipt Note wizard"
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={step === 1}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted disabled:opacity-40 transition-all"
          >
            <Icon name="ChevronLeftIcon" size={14} />
            Back
          </button>
          <div className="flex items-center gap-2">
            {step < 3 ? (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-600 hover:bg-primary/90 transition-all active:scale-95"
              >
                Next
                <Icon name="ChevronRightIcon" size={14} />
              </button>
            ) : (
              <button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95 disabled:opacity-60"
                style={{ minWidth: '160px', justifyContent: 'center' }}
              >
                {isSubmitting ? (
                  <span className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={15} />
                    Submit for Inspection
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={`wiz-step-${s.num}`}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 transition-all ${
                step > s.num ? 'bg-teal text-white' : step === s.num ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {step > s.num ? <Icon name="CheckIcon" size={13} /> : s.num}
              </div>
              <span className={`text-xs font-500 hidden sm:block ${step === s.num ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px ${step > s.num ? 'bg-teal' : 'bg-border'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Header */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Vendor <span className="text-red-500">*</span></label>
              <select className={fieldClass(!!errors.vendor)} {...register('vendor', { required: 'Vendor is required.' })}>
                <option value="">Select vendor…</option>
                {VENDORS.map((v) => <option key={`vend-${v}`} value={v}>{v}</option>)}
              </select>
              {errors.vendor && <p className="mt-1 text-xs text-red-600">{errors.vendor.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Receiving Site <span className="text-red-500">*</span></label>
              <select className={fieldClass(!!errors.site)} {...register('site', { required: 'Site is required.' })}>
                <option value="">Select site…</option>
                {SITES.map((s) => <option key={`site-wiz-${s}`} value={s}>{s}</option>)}
              </select>
              {errors.site && <p className="mt-1 text-xs text-red-600">{errors.site.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">PO Reference</label>
              <p className="text-xs text-muted-foreground mb-1.5">Link to the originating purchase order</p>
              <input type="text" placeholder="PO-2026-XXXX" className={fieldClass(false)} {...register('poReference')} />
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Delivery Date <span className="text-red-500">*</span></label>
              <input type="date" className={fieldClass(!!errors.deliveryDate)} {...register('deliveryDate', { required: 'Delivery date is required.' })} />
              {errors.deliveryDate && <p className="mt-1 text-xs text-red-600">{errors.deliveryDate.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Delivery Note / Challan No.</label>
              <input type="text" placeholder="DN-XXXX" className={fieldClass(false)} {...register('deliveryNote')} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Inspection Notes</label>
            <textarea rows={3} placeholder="Overall condition of delivery, packaging observations, damage notes…" className={`${fieldClass(false)} resize-none`} {...register('notes')} />
          </div>
        </div>
      )}

      {/* Step 2: Line items */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-500 text-foreground">Add received items and quantities</p>
            <button
              type="button"
              onClick={() => append({ itemId: '', receivedQty: 0, rejectedQty: 0, unitCost: 0, batchNumber: '', expiryDate: '' })}
              className="flex items-center gap-1.5 text-xs text-secondary hover:text-secondary/80 font-600"
            >
              <Icon name="PlusIcon" size={13} />
              Add Line
            </button>
          </div>

          <div className="space-y-3">
            {fields.map((field, idx) => {
              const selectedItem = MOCK_ITEMS.find((i) => i.id === lineItems[idx]?.itemId);
              return (
                <div key={field.id} className="border border-border rounded-lg p-4 bg-muted/20 relative">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-600 text-muted-foreground">Line {idx + 1}</span>
                    {fields.length > 1 && (
                      <button type="button" onClick={() => remove(idx)} className="p-1 hover:bg-red-50 text-red-400 rounded transition-colors">
                        <Icon name="TrashIcon" size={13} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-500 text-foreground mb-1">Item <span className="text-red-500">*</span></label>
                      <select className={fieldClass(false)} {...register(`lineItems.${idx}.itemId`)}>
                        <option value="">Select item…</option>
                        {MOCK_ITEMS.filter((i) => i.isActive).map((item) => (
                          <option key={`li-item-${idx}-${item.id}`} value={item.id}>{item.itemCode} — {item.itemName}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-500 text-foreground mb-1">Unit Cost (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={selectedItem?.unitCost.toFixed(2) ?? '0.00'}
                        className={fieldClass(false)}
                        {...register(`lineItems.${idx}.unitCost`, { valueAsNumber: true })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-500 text-foreground mb-1">Received Qty <span className="text-red-500">*</span></label>
                      <input type="number" min="0" placeholder="0" className={fieldClass(false)} {...register(`lineItems.${idx}.receivedQty`, { valueAsNumber: true })} />
                    </div>
                    <div>
                      <label className="block text-xs font-500 text-foreground mb-1">Rejected Qty</label>
                      <input type="number" min="0" placeholder="0" className={fieldClass(false)} {...register(`lineItems.${idx}.rejectedQty`, { valueAsNumber: true })} />
                    </div>
                    <div>
                      <label className="block text-xs font-500 text-foreground mb-1">UOM</label>
                      <input type="text" value={selectedItem?.uom ?? ''} disabled className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-muted text-muted-foreground" readOnly />
                    </div>
                    {(selectedItem?.hasExpiry || selectedItem?.isQhseItem) && (
                      <>
                        <div>
                          <label className="block text-xs font-500 text-foreground mb-1">Batch Number</label>
                          <input type="text" placeholder="BATCH-XXXX" className={fieldClass(false)} {...register(`lineItems.${idx}.batchNumber`)} />
                        </div>
                        <div>
                          <label className="block text-xs font-500 text-foreground mb-1">Expiry Date</label>
                          <input type="date" className={fieldClass(false)} {...register(`lineItems.${idx}.expiryDate`)} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Running total */}
          <div className="flex items-center justify-end gap-4 px-4 py-3 bg-primary/5 border border-primary/15 rounded-lg">
            <span className="text-sm text-muted-foreground">Estimated GRN Total:</span>
            <span className="text-lg font-700 text-foreground tabular-nums">
              € {totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <div className="space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex gap-3">
            <Icon name="InformationCircleIcon" size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              Submitting this GRN will create a PENDING_APPROVAL record. Once approved, the GRN is <strong>immutable</strong> and stock positions will be updated automatically. Reversal is only possible via a RET-OUT transaction.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-3">Header Summary</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Vendor', value: getValues('vendor') || '—' },
                { label: 'Site', value: getValues('site') || '—' },
                { label: 'PO Reference', value: getValues('poReference') || '—' },
                { label: 'Delivery Date', value: getValues('deliveryDate') || '—' },
                { label: 'Delivery Note', value: getValues('deliveryNote') || '—' },
                { label: 'Total Value', value: `€ ${totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}` },
              ].map((m) => (
                <div key={`rev-${m.label}`} className="bg-muted/40 rounded-lg px-3 py-2.5">
                  <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground">{m.label}</p>
                  <p className="text-sm font-500 text-foreground mt-0.5">{m.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-3">Line Items ({fields.length})</h4>
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {['Item', 'Received', 'Rejected', 'Unit Cost', 'Line Total'].map((h) => (
                      <th key={`rev-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lineItems.map((li, idx) => {
                    const item = MOCK_ITEMS.find((i) => i.id === li.itemId);
                    const lineTotal = (li.receivedQty - li.rejectedQty) * (li.unitCost || item?.unitCost || 0);
                    return (
                      <tr key={`rev-li-${idx}`} className="hover:bg-muted/30 transition-colors">
                        <td className="px-3 py-2.5">
                          <p className="text-xs font-600 text-foreground">{item?.itemName ?? '—'}</p>
                          <p className="text-[11px] font-mono-data text-muted-foreground">{item?.itemCode ?? ''}</p>
                        </td>
                        <td className="px-3 py-2.5 text-xs tabular-nums text-teal font-500">{li.receivedQty} {item?.uom ?? ''}</td>
                        <td className="px-3 py-2.5 text-xs tabular-nums text-red-600">{li.rejectedQty > 0 ? `${li.rejectedQty} ${item?.uom ?? ''}` : '—'}</td>
                        <td className="px-3 py-2.5 text-xs tabular-nums text-foreground">€ {(li.unitCost || item?.unitCost || 0).toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-xs tabular-nums font-600 text-foreground">€ {lineTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted/30">
                    <td colSpan={4} className="px-3 py-2.5 text-xs font-600 text-right text-muted-foreground">GRN Total</td>
                    <td className="px-3 py-2.5 text-sm font-700 text-foreground tabular-nums">
                      € {totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {getValues('notes') && (
            <div>
              <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-2">Inspection Notes</h4>
              <p className="text-sm text-foreground bg-muted/40 rounded-lg px-3 py-2.5">{getValues('notes')}</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}